import { SESSION_CONSTANT } from "../../../shared/utils/SessionConstants";
import DeliverableService from "../../DeliverableView/services/DeliverableService";

interface SsoSessionBody {
  userId?: string | null;
  organizationId?: string | null;
}

// What a load did, so a caller can tell the user why nothing changed:
// loaded    - features fetched and stored (replaces any previous value)
// unmatched - the BackEnd answered with null ids, no user profile / organization for this account
// failed    - the feature fetch failed; a previous value, if any, was kept
// skipped   - no ids in the response or an unreadable body; nothing touched
export type SsoLoadResult = "loaded" | "unmatched" | "failed" | "skipped";

const deliverableService = new DeliverableService();

// A failed fetch or null ids must never replace a value that is already there with "[]": that would
// strip permissions the user was legitimately granted. "[]" is only written when nothing is stored yet.
const writeEmptyIfAbsent = (): void => {
  if (localStorage.getItem(SESSION_CONSTANT.FEATURES_KEY) === null) {
    localStorage.setItem(SESSION_CONSTANT.FEATURES_KEY, "[]");
  }
};

// Loads the signed-in SSO user's feature permissions into localStorage["features"], the key and
// shape LoginService writes after a username/password login (a JSON array of feature keys).
//
// The ids come from the BackEnd's own POST /api/auth/sso-session response. When that response
// carries no ids at all (BackEnd not yet extended) nothing is fetched or written, so behaviour is
// exactly what it was before this existed. A successful fetch replaces the stored value; null ids
// or a failed fetch keep an existing value and only default an empty store to [].
export const loadSsoFeatures = async (response: Response): Promise<SsoLoadResult> => {
  let body: SsoSessionBody | null = null;
  try {
    body = (await response.json()) as SsoSessionBody;
  } catch (parseError) {
    console.warn("[MSAL] SSO session response had no readable body; features not loaded:", parseError);
    return "skipped";
  }

  if (!body || body.userId === undefined) {
    return "skipped";
  }

  const { userId, organizationId } = body;
  if (!userId || !organizationId) {
    console.warn("[MSAL] SSO session returned no user/organization ids; existing features kept.");
    writeEmptyIfAbsent();
    return "unmatched";
  }

  try {
    const features = await deliverableService.getAllFeatues(userId, organizationId);
    localStorage.setItem(SESSION_CONSTANT.FEATURES_KEY, JSON.stringify(features));
    return "loaded";
  } catch (featureError) {
    console.warn("[MSAL] Feature fetch failed; existing features kept:", featureError);
    writeEmptyIfAbsent();
    return "failed";
  }
};
